import React, { useState, useRef, useEffect } from 'react';
import DownArrow from '../../assets/down-arrow.png';

function FunderStateFilter({ 
    appliedFunderState, 
    onApply, 
    onReset 
}) {
    const [selectedFunderState, setSelectedFunderState] = useState(appliedFunderState || '');
    const [showFunderStateDropdown, setShowFunderStateDropdown] = useState(false);
    const funderStateDropdownRef = useRef(null);

    // Update local state when props change
    useEffect(() => {
        setSelectedFunderState(appliedFunderState || '');
    }, [appliedFunderState]);

    // US States array
    const usStates = [
        { code: '', name: 'All States' },
        { code: 'AL', name: 'Alabama' },
        { code: 'AK', name: 'Alaska' },
        { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' },
        { code: 'CA', name: 'California' },
        { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' },
        { code: 'DE', name: 'Delaware' },
        { code: 'FL', name: 'Florida' },
        { code: 'GA', name: 'Georgia' },
        { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' },
        { code: 'IL', name: 'Illinois' },
        { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' },
        { code: 'KS', name: 'Kansas' },
        { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' },
        { code: 'ME', name: 'Maine' },
        { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' },
        { code: 'MI', name: 'Michigan' },
        { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' },
        { code: 'MO', name: 'Missouri' },
        { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' },
        { code: 'NV', name: 'Nevada' },
        { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' },
        { code: 'NM', name: 'New Mexico' },
        { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' },
        { code: 'ND', name: 'North Dakota' },
        { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' },
        { code: 'OR', name: 'Oregon' },
        { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' },
        { code: 'SC', name: 'South Carolina' },
        { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' },
        { code: 'TX', name: 'Texas' },
        { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' },
        { code: 'VA', name: 'Virginia' },
        { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' },
        { code: 'WI', name: 'Wisconsin' },
        { code: 'WY', name: 'Wyoming' }
    ];

    // Helper function to format funder state filter for display
    const formatFunderStateFilter = (stateCode) => {
        if (!stateCode || stateCode === '') {
            return "Funder State";
        }
        const state = usStates.find(s => s.code === stateCode);
        return `Funder State: ${state ? state.name : stateCode}`;
    };

    // Handle clicking outside funder state dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (funderStateDropdownRef.current && !funderStateDropdownRef.current.contains(event.target)) {
                setShowFunderStateDropdown(false);
            }
        };

        if (showFunderStateDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showFunderStateDropdown]);

    const handleApply = () => {
        onApply(selectedFunderState);
        setShowFunderStateDropdown(false);
    };

    const handleReset = () => {
        setSelectedFunderState('');
        onReset();
        setShowFunderStateDropdown(false);
    };

    return (
        <div className="relative">
            <button 
                className={`px-4 py-2 rounded-md flex flex-row gap-2 items-center ${
                    appliedFunderState && appliedFunderState !== ''
                        ? 'text-black shadow-sm border border-gray-500'
                        : 'border border-gray-500 hover:bg-gray-100 shadow-sm'
                }`}
                onClick={() => setShowFunderStateDropdown(!showFunderStateDropdown)}
            >
                {formatFunderStateFilter(appliedFunderState)}
                <img src={DownArrow} alt="Down Arrow" className='w-4 h-4' />
            </button>
            {showFunderStateDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 z-10 min-w-80 max-h-96 overflow-y-auto" ref={funderStateDropdownRef}>
                    <h3 className="font-semibold mb-3">Funder State</h3>
                    
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">
                            Select funder state:
                        </label>
                        <select
                            value={selectedFunderState}
                            onChange={(e) => setSelectedFunderState(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            {usStates.map((state) => (
                                <option key={state.code} value={state.code}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                            Filter awards to show only those from funders in the selected state
                        </p>
                    </div>
                    
                    {/* Apply Button */}
                    <div className="flex justify-end gap-2">
                        <button
                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                        <button
                            className="px-3 py-1 text-sm bg-indigo-500 text-white rounded hover:bg-indigo-600"
                            onClick={handleApply}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FunderStateFilter; 